import { AfterContentChecked, Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AccessRoleTypes } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { NavigationActions, ParentNavigationItem } from '@next/core-lib/navigation';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
})
export class AppComponent implements OnInit, AfterContentChecked {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);

  navigationItems: ParentNavigationItem[] = [];
  private readonly grainRegistrationRelativeUrl = '/history';
  private readonly pickUpRelativeUrl = '/pick-up';
  private readonly grainRegistrationNavigationItem: ParentNavigationItem = {
    relativeUrl: this.grainRegistrationRelativeUrl,
    label: 'sidenav.grain-registration',
    icon: 'dashboard',
  };
  private readonly pickUpNavigationItem: ParentNavigationItem = {
    relativeUrl: this.pickUpRelativeUrl,
    label: 'sidenav.grain-pick-up',
    icon: 'dashboard',
  };
  private readonly grainConditionsNavigationItem: ParentNavigationItem = {
    absoluteUrl: 'https://www.agrifirm.nl/Graan_en_peulvruchtencondities_2026/',
    label: 'sidenav.grain-conditions',
    newWindow: true,
    hideNewWindowIcon: true,
  };

  ngOnInit(): void {
    this.initializeNavigation();
  }

  /* By default, the application is initialized with Grain Registration page
   * A redirect is forced to Grain PickUp page, if the user has no access to Grain Registration */
  ngAfterContentChecked(): void {
    this.redirectToGrainPickUp();
  }

  private initializeNavigation(): void {
    const userInitialized$ = this.store.select(UserSelectors.selectUserInitialized);
    const grainRegistrationRule$ = this.store
      .select(AuthorizationSelectors.selectAccessRuleForApplicationKey('Aanmelden'))
      .pipe(filter((rule) => rule !== undefined));
    const pickUpRule$ = this.store
      .select(AuthorizationSelectors.selectAccessRuleForApplicationKey('GrainPickUp'))
      .pipe(filter((rule) => rule !== undefined));

    combineLatest([userInitialized$, grainRegistrationRule$, pickUpRule$])
      .pipe(
        filter(([userInitialized]) => userInitialized),
        take(1),
      )
      .subscribe(([_, grainRegistrationRule, pickUpRule]) => {
        this.navigationItems = [
          ...(grainRegistrationRule.RoleKey !== AccessRoleTypes.NotAuthorized &&
          grainRegistrationRule.RoleKey !== AccessRoleTypes.Transporter
            ? [this.grainRegistrationNavigationItem]
            : []),
          ...(pickUpRule.RoleKey !== AccessRoleTypes.NotAuthorized
            ? [this.pickUpNavigationItem]
            : []),
          this.grainConditionsNavigationItem,
        ];

        this.store.dispatch(
          NavigationActions.setNavigationItems({ navigationItems: this.navigationItems }),
        );
      });
  }

  private redirectToGrainPickUp() {
    const relativeItems = this.navigationItems.filter((item) => item.relativeUrl);
    const currentNavigationId = this.router.currentNavigation()?.id;

    if (
      relativeItems.length === 1 &&
      relativeItems[0].relativeUrl === this.pickUpRelativeUrl &&
      this.router.url !== this.pickUpRelativeUrl &&
      currentNavigationId !== undefined &&
      currentNavigationId < 5 // This gives the number of tries to navigate.
      /* The higher the number, it means the navigation was not successful.
       * This is needed because when the access token expires it needs to navigate to login page to refresh it. */
    ) {
      this.router.navigate([this.pickUpRelativeUrl]);
    }
  }
}
